import React, { useCallback, useEffect, useMemo } from 'react'
import { pathOr } from 'ramda'
import { useDispatch, useSelector } from 'react-redux'
import { FormField, Select, TextField, TextInput } from '../../../components'
import { formUtils } from '../../../../utils'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import { $TSFixMe } from 'types/ts-migrate'
import { FormikErrors, FormikTouched } from 'formik'
import { userApiSelectors } from 'modules/api/user'
import { walletApiEvents, walletApiSelectors } from 'modules/api/wallet'
import { useEffectOnce } from 'react-use'

type TProps = {
  values: {
    bank: string
    accountNumber: string
    accountType: string
    branchCode?: string
    accountName?: string
  }
  valueRoot?: string
  handleChange: (e: React.ChangeEvent<any>) => void
  handleBlur: (e: React.FocusEvent<any>) => void
  setFieldValue: (field: string, value: any) => void
  setFieldTouched: (field: string, isTouched: boolean) => void
  accountDescription?: boolean
  // Marks account number and account holder required, and renders the latter
  requireBankDetails?: boolean
  errors: FormikErrors<$TSFixMe>
  touched: FormikTouched<$TSFixMe>
  submitCount?: number
  disableFields?: boolean | undefined
}

type TBank = {
  name: string
  branchCode: string
  supportsInstantPayments: boolean
  aliases: string[]
}

const MAJOR_BANK_NAMES = ['FNB', 'ABSA', 'Standard Bank', 'Nedbank', 'Capitec', 'Investec']

const BankDetails = ({
  values,
  valueRoot,
  handleChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  accountDescription,
  requireBankDetails,
  errors,
  touched,
  submitCount,
  disableFields,
}: TProps) => {
  const dispatch = useDispatch()

  const fetchBanks = useCallback(() => {
    dispatch(walletApiEvents.banks_request())
  }, [dispatch])

  useEffectOnce(() => {
    fetchBanks()
  })

  const availableBanks = useSelector(walletApiSelectors.getBanks)

  // The endpoint responds with { banks: [...] }; tolerate a bare array too in
  // case the selector is changed to unwrap it later.
  const banks: TBank[] = useMemo(
    () => (Array.isArray(availableBanks) ? availableBanks : pathOr([], ['banks'], availableBanks)),
    [availableBanks],
  )

  const fieldName = (field: any) => {
    const path = valueRoot ? [valueRoot, field] : [field]
    return path.join('.')
  }

  // Flatten grouped ({ label, options }) or flat option lists into a single
  // array of options so we can resolve the currently selected value regardless
  // of whether the list is grouped.
  const flattenOptions = (options: any[]): any[] =>
    options.flatMap((o: any) => (Array.isArray(o.options) ? o.options : [o]))

  const getSelectValue = (options: any, key: any) => flattenOptions(options).find((o: any) => o.value === values[key])
  const getFieldValue = (name: any) => pathOr('', [name], values)

  // Auto-touch the complementary field when one gets a value. Not needed when
  // both are always required, and would show errors on mount.
  useEffect(() => {
    if (requireBankDetails) return

    const accountNumberValue = String(getFieldValue('accountNumber')).trim()
    const accountNameValue = String(getFieldValue('accountName')).trim()

    if (accountNumberValue && !pathOr(false, [fieldName('accountName')], touched)) {
      setFieldTouched(fieldName('accountName'), true)
    }
    if (accountNameValue && !pathOr(false, [fieldName('accountNumber')], touched)) {
      setFieldTouched(fieldName('accountNumber'), true)
    }
  }, [values.accountNumber, values.accountName])

  const selectHandler =
    (field: any) =>
    (e: any): void =>
      setFieldValue(fieldName(field), e.value)

  // Universal branch codes keyed by the bank option value
  const bankBranchCodes: Record<string, string> = useMemo(
    () =>
      Object.fromEntries(
        banks.flatMap(bank => [
          [bank.name, bank.branchCode],
          ...(bank.aliases ?? []).map(alias => [alias, bank.branchCode]),
        ]),
      ),
    [banks],
  )

  useEffect(() => {
    const bank = getFieldValue('bank')
    if (!bank) return
    const derived = bankBranchCodes[bank] ?? ''
    if (derived && getFieldValue('branchCode') !== derived) {
      setFieldValue(fieldName('branchCode'), derived)
    }
  }, [values.bank, bankBranchCodes])

  const bankHandler = (e: any): void => {
    setFieldValue(fieldName('bank'), e.value)
    setFieldValue(fieldName('branchCode'), bankBranchCodes[e.value] ?? '')
  }

  const getFieldError = formUtils.getFieldError(submitCount, touched, errors)

  const isValidSupportStaff = useSelector(userApiSelectors.isValidSupportStaff)

  const bankOptions = useMemo(() => {
    const toOption = (bank: TBank) => ({ value: bank.name, label: bank.name })

    const majorBanks = MAJOR_BANK_NAMES.map(name => banks.find(bank => bank.name === name))
      .filter((bank): bank is TBank => Boolean(bank))
      .map(toOption)

    const otherBanks = banks
      .filter(bank => !MAJOR_BANK_NAMES.includes(bank.name))
      .map(toOption)
      .sort((a, b) => a.label.localeCompare(b.label))

    return [
      ...(majorBanks.length ? [{ label: 'Major banks', options: majorBanks }] : []),
      ...(otherBanks.length ? [{ label: 'Other banks', options: otherBanks }] : []),
    ]
  }, [banks])

  const accountTypeOptions = [
    {
      value: 'Cheque',
      label: 'Cheque',
    },
    {
      value: 'Savings',
      label: 'Savings',
    },
    ...(isValidSupportStaff
      ? [
          {
            value: 'PAAF Cheque',
            label: 'Cheque (Standard EFT)',
          },
          {
            value: 'PAAF Savings',
            label: 'Savings (Standard EFT)',
          },
        ]
      : []),
  ]

  // Until the banks request resolves — or if a stored bank is no longer returned
  // by the endpoint — show the stored value rather than an empty select.
  const storedBank = String(getFieldValue('bank'))
  const bankSelectValue =
    getSelectValue(bankOptions, 'bank') ?? (storedBank ? { value: storedBank, label: storedBank } : null)

  // Account number and account holder are mutually required: filling in one
  // makes the other required (validation enforced in the form's yup schema).
  const isAccountNumberRequired = requireBankDetails || String(getFieldValue('accountName')).trim().length > 0
  const isAccountNameRequired = requireBankDetails || String(getFieldValue('accountNumber')).trim().length > 0

  return (
    <div>
      <div className="rbn--row">
        <div className="rbn--col-md-4">
          <FormField>
            <Select
              isSearchable
              options={bankOptions}
              name={fieldName('bank')}
              label="Bank"
              isDisabled={disableFields}
              error={getFieldError(fieldName('bank'))}
              value={bankSelectValue}
              onBlur={handleBlur}
              onChange={bankHandler}
            />
          </FormField>
        </div>
        {getFieldValue('bank') && (
          <div className="rbn--col-md-4">
            <FormField>
              <TextField
                inputComponent={
                  <TextInput
                    type={TextFieldTypes.text}
                    name={fieldName('branchCode')}
                    value={getFieldValue('branchCode')}
                    disabled
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                }
                label="Branch code"
                labelHint="The bank you select determines the universal branch code used for outgoing payments"
                error={getFieldError(fieldName('branchCode'))}
              />
            </FormField>
          </div>
        )}
      </div>
      <div className="rbn--row">
        <div className="rbn--col-md-4">
          <FormField>
            <Select
              options={accountTypeOptions}
              name={fieldName('accountType')}
              label="Account type"
              isDisabled={disableFields}
              error={getFieldError(fieldName('accountType'))}
              value={getSelectValue(accountTypeOptions, 'accountType')}
              onBlur={handleBlur}
              onChange={selectHandler('accountType')}
            />
          </FormField>
        </div>
        <div className="rbn--col-md-4">
          <FormField>
            <TextField
              inputComponent={
                <TextInput
                  type={TextFieldTypes.text}
                  name={fieldName('accountNumber')}
                  value={getFieldValue('accountNumber')}
                  disabled={disableFields}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              }
              label="Account number"
              required={isAccountNumberRequired}
              error={getFieldError(fieldName('accountNumber'))}
            />
          </FormField>
        </div>
      </div>
      <div className="rbn--row">
        {(accountDescription || requireBankDetails) && (
          <div className="rbn--col-md-8">
            <FormField>
              <TextField
                inputComponent={
                  <TextInput
                    type={TextFieldTypes.text}
                    name={fieldName('accountName')}
                    value={getFieldValue('accountName')}
                    disabled={disableFields}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                }
                label="Account holder"
                required={isAccountNameRequired}
                error={getFieldError(fieldName('accountName'))}
              />
            </FormField>
          </div>
        )}
      </div>
    </div>
  )
}

export default BankDetails
